import React, { useState, useEffect } from 'react';

// Material UI
import { Grid, Typography, Paper, Divider } from "@mui/material";

//Components
import Dropdown from "../../../components/inputs/dropdown";
import Input from "../../../components/inputs/input";
import Spinner from "../../../components/spinner/index";

//Hooks
import useNotify from "../../../hooks/notify";
import useUser from '../../../hooks/user/useUser';

//Other
import { cashTypes, cardTypeFormat } from "../../../models/CashRegisterMovementEntity";
import useStyles from "../styles";
import PaymentService from '../../../services/paymentService';
import { utils } from '../../../utils/utils';
import { paymentMethods } from '../../../models/PaymentMethods';
import { PaymentStates } from '../../../models/PaymentStates';
import { paymentMethodTypes, paymentMethodTypesFormat } from '../../../models/InternalVoucherEntity';
import Autocomplete from '../../../components/inputs/Autocomplete';

const defaultPayment = {
    default: true,
    display: 'Selecciona un medio de pago'
};

const initPayment = {
    cashType: '',
    bin: '',
    lastCardDigits: '',
    cuponNumber: '',
    cashTypeId: '',
    bankId: '',
    planId: '',
    installmentId: '',
    plan: '',
    cardRefId: '',
    planRefId: '',
    amount: 0,
    conversionRate: 0
}

const PaymentDetail = ({ title, payments, options, originPayment, onChange = () => { }, onPaymentSelected = () => { } }) => {
    const { classes } = useStyles();
    const { business, branch } = useUser();
    const { Notify, variants } = useNotify();

    const [form, setForm] = useState(initPayment);

    const [paymentSelected, setPaymentSelected] = useState(defaultPayment);
    const [bankSelected, setBankSelected] = useState({});
    const [loading, setLoading] = useState(false);
    const [banks, setBanks] = useState([]);
    const [cards, setCards] = useState([]);
    const [plans, setPlans] = useState([]);
    const [bankFilters, setBankFilters] = useState({});

    useEffect(() => {
        if (originPayment?.currency) {
            handleFormChange({ amount: 0, conversionRate: 0 })
        }
    }, [originPayment?.currency, form.currency]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleFormChange = (newValues) => {
        const values = { ...form, ...newValues }
        setForm(values)
        onChange(values)
    }

    useEffect(() => {
        if (!!business?.defaultCurrency && !form.currency)
            setForm(state => ({ ...state, currency: business.defaultCurrency }))
    }, [business, form.currency])

    useEffect(() => {
        if (form.bankId && banks.length > 0) {
            const bank = banks.find(bank => bank?.id === form?.bankId);
            setBankSelected(bank);
        }
    }, [form.bankId, banks])

    useEffect(() => {
        const getFromServer = async () => {
            const pageSize = payments && payments.length > 0
                ? 100
                : 10;

            setLoading(true);
            PaymentService
                .GetBanks(pageSize, 1, { ...bankFilters, country: business.country })
                .then(res => setBanks(res.values))
                .finally(() => setLoading(false))
        }

        if (form.cashType === cashTypes.card && !!business.country) {
            getFromServer()
        }
    }, [form.cashType, bankFilters, payments, business])

    useEffect(() => {
        const getCardsForBank = async () => {
            setLoading(true);
            PaymentService
                .GetAllCardsByBankId(form.bankId)
                .then(res => setCards(res))
                .finally(() => setLoading(false))
        }

        if (form.bankId)
            getCardsForBank()
    }, [form.bankId])

    useEffect(() => {
        const getFromServer = async () => {
            setLoading(true);
            PaymentService
                .GetPlansByCardAndBankId(form.bankId, form.cashTypeId)
                .then(res => {
                    setPlans(res.planInstallmentResponse.sort((a, b) => a.name > b.name ? 1 : -1));
                    const plan = res.planInstallmentResponse.find(plan => plan.planId === form.planId);

                    handleFormChange({
                        plan: plan?.id || '',
                        planRefId: plan?.code || '',
                        cardRefId: res.card?.refId || ''
                    })
                })
                .finally(() => setLoading(false))
        }

        if (form.bankId > 0 && form.cashTypeId > 0)
            getFromServer();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.bankId, form.cashTypeId]);

    const handleSelectPayment = (paymentSelected) => {
        if (!paymentSelected.default) {
            const form = {
                id: paymentSelected.paymentId,
                cashType: paymentMethodTypes[paymentSelected.paymentMethodType] || '',
                bin: paymentSelected.binNumber ? `${paymentSelected.binNumber}` : '',
                lastCardDigits: paymentSelected.lastCardDigits !== null && paymentSelected.lastCardDigits !== undefined
                    ? utils.zeroPad(paymentSelected.lastCardDigits, 4)
                    : '',
                cuponNumber: paymentSelected.cuponNumber || '',
                cashTypeId: paymentSelected.cardId || '',
                bankId: paymentSelected.bankId || '',
                planId: paymentSelected.planId || '',
                installmentId: paymentSelected.installmentId || '',
                plan: `${paymentSelected.planId}-${paymentSelected.installmentId}`,
                state: paymentSelected.state,
                currency: paymentSelected.currency || business.defaultCurrency,
                paidAmount: paymentSelected.paidAmount,
                amount: paymentSelected.amount,
                changeAmount: paymentSelected.change
            }

            handleFormChange(form)
        } else {
            handleFormChange(initPayment);
            setBankSelected({})
            setCards([])
            setPlans([])
        }

        setPaymentSelected(paymentSelected);
        onPaymentSelected(paymentSelected);
    }

    const mapPayments = (payments) => {
        const paymentsMap = payments
            .filter(p => p.state === PaymentStates.Approved)
            .filter(p =>
                p.paymentMethodType === paymentMethods.Posnet ||
                p.paymentMethodType === paymentMethods.Cash)
            .map(p => ({
                ...p,
                display: `${paymentMethodTypesFormat[p.paymentMethodType]} ${p.currency} ${utils.formatMoney(p.paidAmount)}`
            }));

        return [...paymentsMap];
    }

    const handleInputChange = (event) =>
        handleFormChange({ [event.target.name]: event.target.value });

    const handlerBinChange = (event) => {
        const value = event.target.value;
        const name = event.target.name;

        if (value.length <= 6)
            handleFormChange({ [name]: value });

        if (value.length === 6) {
            setLoading(true);
            PaymentService
                .GetBin(parseInt(value), branch.country)
                .then((res) => handleFormChange({
                    [name]: value,
                    bankId: res.bankId,
                    cashTypeId: res.cardId,
                    plan: ''
                }))
                .catch(() => {
                    handleFormChange({
                        [name]: value,
                        bankId: '',
                        cashTypeId: '',
                        plan: ''
                    })
                    Notify("No se encontro el bin ingresado.", variants.ERROR);
                })
                .finally(() => setLoading(false));
        }
    }

    const handleBankChange = (event, option) => {
        handleFormChange({
            bankId: option?.id,
            cashTypeId: '',
            cardRefId: '',
            plan: '',
            planId: '',
            planRefId: '',
            installmentId: ''
        });

        setBankSelected(option);
        setCards([]);
        setPlans([]);
    }

    const handleCardChange = (event) => {
        const card = cards.find(card => card.id === event.target.value);

        setPlans([])
        handleFormChange({
            cashTypeId: card.id,
            cardRefId: card.refId,
            plan: '',
            planId: '',
            planRefId: '',
            installmentId: ''
        })
    }

    const handlePlanChange = (event) => {
        const plan = plans.find(plan => plan.id === event.target.value);

        handleFormChange({
            plan: plan.id,
            planId: plan.planId,
            planRefId: plan.code,
            installmentId: plan.installmentId
        })
    }

    const handleAmountDestinationChange = (event) => {
        const amount = event.target.value;
        if (!utils.ValidateValueIsString(amount) && utils.ValidateDecimalFormat(amount))
            if (amount >= 0) {
                const conversionRate = originPayment.amount > 0
                    ? parseFloat((originPayment.paidAmount / amount).toFixed(6))
                    : 0;
                handleFormChange({ amount, conversionRate });
            }
    }

    const handleConversionRateChange = (event) => {
        const conversionRate = event.target.value;
        if (!utils.ValidateValueIsString(conversionRate) && utils.ValidateDecimalFormat(conversionRate))
            if (conversionRate >= 0) {
                const amount = parseFloat((originPayment.paidAmount / conversionRate).toFixed(6));
                handleFormChange({ amount, conversionRate });
            }
    }

    let timerId;
    const handleBankAutocompleteInputChange = (event, value) => {
        if (event?._reactName && (event?._reactName !== 'onClick' || value === "")) {
            clearTimeout(timerId);
            timerId = setTimeout(() => setBankFilters({ ...bankFilters, name: value }), 500);
        }
    }

    return (
        <Paper className={classes.paper}>
            {loading && <Spinner lockScreen />}
            <Grid container spacing={1}>
                <Grid size={12}>
                    <Typography variant='h5' align='center'>
                        {title}
                    </Typography>
                </Grid>
                <Grid size={12}>
                    <Divider />
                </Grid>
                {
                    payments &&
                    <Grid size={12}>
                        <Autocomplete
                            fullWidth
                            disabled={payments.length === 0}
                            multiple={false}
                            disableClearable
                            size='small'
                            name='payment'
                            label='Pago realizado'
                            onChange={(e, value) => handleSelectPayment(value)}
                            getOptionLabel={option => option.display}
                            value={paymentSelected}
                            options={mapPayments(payments)}
                        />
                    </Grid>
                }
                {
                    (!payments || !paymentSelected.default) &&
                    <Grid size={12}>
                        <Dropdown
                            fullWidth
                            size='small'
                            name='cashType'
                            label='Medio de pago'
                            onChange={handleInputChange}
                            value={form.cashType}
                            values={options}
                            disabled={options.length <= 0 || !paymentSelected.default}
                        />
                    </Grid>
                }
                {
                    form.cashType === cashTypes.card &&
                    <>
                        <Grid size={6}>
                            <Input
                                disabled={!paymentSelected.default || loading}
                                fullWidth
                                size='small'
                                name="bin"
                                label="Ingresa el bin de la tarjeta"
                                onChange={handlerBinChange}
                                value={form.bin}
                            />
                        </Grid>
                        <Grid size={6}>
                            <Input
                                fullWidth
                                disabled={!paymentSelected.default}
                                name='lastCardDigits'
                                size='small'
                                type='number'
                                label="Ultimos 4 digitos"
                                onChange={handleInputChange}
                                value={form.lastCardDigits}
                                InputProps={{
                                    inputProps: {
                                        min: 0
                                    }
                                }}
                            />
                        </Grid>
                        <Grid size={6}>
                            <Autocomplete
                                fullWidth
                                disabled={!paymentSelected.default || loading}
                                size='small'
                                name='bankId'
                                TextProps={{ placeholder: 'Banco' }}
                                onChange={handleBankChange}
                                onInputChange={handleBankAutocompleteInputChange}
                                getOptionLabel={(option) => option?.name || ''}
                                value={bankSelected || ''}
                                options={banks || []}
                            />

                        </Grid>
                        <Grid size={6}>
                            <Dropdown
                                fullWidth
                                disabled={!paymentSelected.default || loading}
                                size='small'
                                name='cashTypeId'
                                label='Tarjeta'
                                onChange={handleCardChange}
                                getOptionLabel={(option) => `${option?.name} ${option?.type && cardTypeFormat[option.type]}`}
                                getOptionValue={(option) => option?.id}
                                value={form.cashTypeId}
                                values={cards}
                            />
                        </Grid>
                        <Grid size={6}>
                            <Dropdown
                                fullWidth
                                disabled={!paymentSelected.default || loading}
                                size='small'
                                name='plan'
                                label='Plan'
                                getOptionLabel={(option) => `${option?.name}`}
                                getOptionValue={(option) => option?.id}
                                onChange={handlePlanChange}
                                value={form.plan}
                                values={plans}
                            />
                        </Grid>
                        <Grid size={6}>
                            <Input
                                fullWidth
                                disabled={!paymentSelected.default}
                                size='small'
                                name='cuponNumber'
                                type='number'
                                label="Numero cupón"
                                onChange={handleInputChange}
                                value={form.cuponNumber}
                                InputProps={{
                                    inputProps: {
                                        min: 0
                                    }
                                }}
                            />
                        </Grid>
                    </>
                }
                {
                    (form.cashType === cashTypes.qr ||
                        form.cashType === cashTypes.link) &&
                    <Grid size={12}>
                        <Input
                            fullWidth
                            disabled={!paymentSelected.default}
                            size='small'
                            name='cuponNumber'
                            type='number'
                            label="Numero cupón"
                            onChange={handleInputChange}
                            value={form.cuponNumber}
                            InputProps={{
                                inputProps: {
                                    min: 0
                                }
                            }}
                        />
                    </Grid>
                }
                {
                    form.cashType === cashTypes.cash &&
                    <Grid size={12}>
                        <Grid container spacing={1}>
                            <Grid size={12}>
                                <Dropdown
                                    fullWidth
                                    size="small"
                                    name="currency"
                                    label="Seleccione una monedas"
                                    placeholder="Moneda"
                                    onChange={handleInputChange}
                                    values={business.currencies?.filter(c => originPayment?.cashType !== cashTypes.cash || c !== originPayment?.currency)}
                                    value={form.currency}
                                    getOptionLabel={(option) => option}
                                    getOptionValue={(option) => option}
                                    disabled={!paymentSelected.default}
                                />
                            </Grid>
                            {!!originPayment && originPayment.currency !== form.currency &&
                                <>
                                    <Grid size={6}>
                                        <Input
                                            required
                                            fullWidth
                                            size='small'
                                            key="amountDestination"
                                            name="amountDestination"
                                            label={`Monto destino ${form.currency}`}
                                            onChange={handleAmountDestinationChange}
                                            onFocus={(e) => e.target.select()}
                                            value={form?.amount || '0'}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <Input
                                            required
                                            fullWidth
                                            size='small'
                                            key="conversionRate"
                                            name="conversionRate"
                                            label={`Cotización ${form?.conversionRate > 0
                                                ? `(${form.currency} 1 = ${originPayment.currency} ${utils.formatMoney(form.conversionRate)})`
                                                : ""}`}
                                            onChange={handleConversionRateChange}
                                            onFocus={(e) => e.target.select()}
                                            value={form?.conversionRate || '0'}
                                        />
                                    </Grid>
                                </>
                            }
                        </Grid>
                    </Grid>
                }
            </Grid>
        </Paper>
    );
}

export default PaymentDetail;